"use client";

import { useEffect, useState } from "react";
import { clientLogger } from "@/lib/logger";
import {
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerRoot,
	DrawerTrigger,
} from "@/components/ui/drawer";
import { Search } from "lucide-react";
import { fetchData, getGroupedData } from "@/app/product/utils/car.utils";
import { handleInputChange } from "../../app/product/utils/search.utils";
import Dropdown from "./Dropdown";
import Image from "next/image";
import { useAllowedProductTypes } from "@/hooks/useProductType";
import { PRODUCT_NAME } from "@/constants/productTypes";
import { useAppData } from "@/store/appStore";
import ProductCallbackRequest from "@/components/request-callback/ProductCallbackRequest";

export const SearchModal = ({
	color,
	type,
	tab,
}: {
	color?: string;
	type?: "searchBar" | "header" | "compact-dark";
	tab?: "devices" | "cars";
}) => {
	const [searchValue, setSearchValue] = useState("");
	const [searchData, setSearchData] = useState<Car[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [groupedData, setGroupedData] = useState<GroupedData>({
		car: {},
		mobile: {},
	});
	const [isInitialized, setIsInitialized] = useState(false);

	const [appData] = useAppData();
	const productType = useAllowedProductTypes();
	const allowedProductNames = productType.map((type) => PRODUCT_NAME[type]);

	// Get enabled device subtypes for search filtering
	const enabledSubtypes = Object.entries(appData?.deviceSubtypes || {})
		.filter(([, enabled]) => enabled)
		.map(([key]) => key);
	const allSubtypesEnabled = Object.values(appData?.deviceSubtypes || {}).every(
		(v) => v,
	);
	const subtypeParam =
		!allSubtypesEnabled && enabledSubtypes.length > 0
			? enabledSubtypes
			: undefined;

	const displayName =
		allowedProductNames.length === 1
			? allowedProductNames[0]
			: allowedProductNames.join(" and ");

	const displayName2 =
		allowedProductNames.length === 1
			? allowedProductNames[0]
			: allowedProductNames.join(", ");

	useEffect(() => {
		const cookies = document?.cookie?.split("; ");
		const searchValueCookie = cookies?.find((cookie) =>
			cookie?.startsWith("searchValue="),
		);
		if (searchValueCookie) {
			const existingSearchValue = searchValueCookie?.split("=")[1];
			setSearchValue(existingSearchValue);
		}
		setIsInitialized(true);
	}, []);

	useEffect(() => {
		if (!isInitialized) return;
		const expires = new Date(Date.now() + 30 * 86400e3).toUTCString(); // expires in 30 days
		if (searchValue?.length > 0) {
			clientLogger.debug("Fetching search data", {
				component: "SearchModal",
				searchValue,
				productType,
			});
			setIsLoading(true);
			(async () => {
				try {
					await fetchData({
						searchValue,
						productType,
						subtype: subtypeParam,
						setData: setSearchData,
					});
				} catch (error) {
					const err =
						error instanceof Error ? error : new Error("Unknown search error");
					clientLogger.error("Search data fetch failed", err, {
						component: "SearchModal",
					});
				} finally {
					setIsLoading(false);
				}
			})();
			document.cookie = `searchValue=${searchValue}; expires=${expires}; path=/`;
		} else {
			setIsLoading(false);
			setSearchData([]);
			document.cookie = `searchValue=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		}
	}, [searchValue, productType, isInitialized]);

	useEffect(() => {
		if (searchData?.length > 0) {
			getGroupedData({ data: searchData, setData: setGroupedData });
		} else {
			setGroupedData({ car: {}, mobile: {} });
		}
	}, [searchData, setGroupedData]);

	return (
		<DrawerRoot
			size={"full"}
			placement={"bottom"}
			open={isOpen}
			onOpenChange={(details) => setIsOpen(details?.open)}
		>
			<DrawerTrigger
				color={color}
				onClick={() => {
					setIsOpen(true);
					clientLogger.info("Search modal opened", {
						component: "SearchModal",
						type,
						tab,
					});
				}}
				w={"full"}
			>
				{type === "header" ? (
					<div
						id="header_search"
						className="flex items-center gap-2 flex-1 h-[52px] p-4 rounded-[10px] bg-white"
					>
						<Search size={20} className="text-header-gray" />
						<p className="text-text-gray text-sm">Search for {displayName}..</p>
					</div>
				) : type === "compact-dark" ? (
					<div
						id="search_compact_dark"
						className="flex items-center gap-2 h-[45px] px-3 rounded-xl bg-automint-dark w-full"
					>
						<Search size={18} className="text-gray-400" />
						<p className="text-gray-400 text-sm truncate">
							Search for {displayName}..
						</p>
					</div>
				) : (
					<div
						id="search"
						className="flex border rounded-xl border-[#c4c4c4] items-center h-14 bg-white w-full px-4"
					>
						<div className="text-[#AEAEAE]">
							<Search size={20} />
						</div>
						<p className="text-[#AEAEAE] placeholder-gray-400 outline-none bg-transparent pl-2">
							Search for {displayName}..
						</p>
					</div>
				)}
			</DrawerTrigger>
			<DrawerContent
				bgColor={"#ffffff"}
				transitionDuration={"faster"}
				width={"100vw"}
				maxWidth={"100vw"}
				height={"100vh"}
				marginX={0}
				borderTopRadius={"0"}
			>
				<DrawerHeader padding={0}>
					<section
						className="flex items-center gap-3 md:px-64 bg-automint-black px-4 py-6 w-full z-10 scrollbar-hide"
						// style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)" }}
					>
						<div className="flex-shrink-0">
							<Image
								src="/search/CaretLeft.svg"
								alt="Logo"
								width={45}
								height={45}
								onClick={() => setIsOpen(false)}
							/>
						</div>
						<div className="flex items-center gap-3 flex-1">
							<div className="flex items-center gap-2 flex-1 h-[52px] px-4 rounded-[16px] bg-automint-dark">
								<Search size={20} className="text-gray-500" />
								<input
									id="search_input"
									type="text"
									placeholder={`Search ${displayName2} and brands`}
									className="w-full text-sm text-white placeholder:text-gray-500 outline-none bg-transparent"
									autoComplete="off"
									value={searchValue}
									onChange={(e) => {
										handleInputChange({ event: e, setData: setSearchValue });
										clientLogger.debug("Search input changed", {
											component: "SearchModal",
											value: e.target.value,
										});
									}}
								/>
							</div>
							{searchValue?.length > 0 && (
								<div className="flex-shrink-0">
									<Image
										src="/XCircle.svg"
										alt="Clear search"
										width={25}
										height={25}
										onClick={() => {
											setSearchValue("");
											clientLogger.info("Search input cleared", {
												component: "SearchModal",
											});
										}}
									/>
								</div>
							)}
						</div>
					</section>
				</DrawerHeader>
				<DrawerBody padding={0}>
					{isLoading ? (
						<LoadingShimmer />
					) : (
						<section className="px-4 pt-6 md:px-64 scrollbar-hide">
							{searchValue?.length > 0 && searchData?.length === 0 ? (
								// Empty state with callback component
								<div className="flex flex-col items-center justify-center py-12">
									<div className="text-center mb-6">
										<div className="mb-4">
											<svg
												width="64"
												height="64"
												viewBox="0 0 64 64"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												className="mx-auto text-gray-400"
											>
												<rect
													x="12"
													y="12"
													width="40"
													height="40"
													rx="4"
													stroke="currentColor"
													strokeWidth="2"
												/>
												<path
													d="M24 24L40 40M40 24L24 40"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
												/>
											</svg>
										</div>
										<h3 className="text-xl font-semibold text-automint-text mb-2">
											Nothing pops up for that search
										</h3>
										<p className="text-gray-600">
											Try a different keyword or tweak the spelling.
										</p>
									</div>

									{/* Callback component for empty state */}
									<div className="w-full max-w-2xl">
										<ProductCallbackRequest
											productName={`User searched for "${searchValue}"`}
											triggerText="Request callback"
											showWrapper={true}
											layout="inline"
										/>
									</div>
								</div>
							) : (
								<>
									<Dropdown
										assetItemId="search_asset_item"
										assetListId="search_asset_list"
										searchValue={searchValue}
										searchData={searchData}
										groupedData={groupedData}
										setIsOpen={setIsOpen}
										type="linked"
									/>

									{/* Show callback component at bottom when there are results AND user has typed something */}
									{searchValue?.length > 0 && searchData?.length > 0 && (
										<div className="mt-6 mb-8">
											<ProductCallbackRequest
												productName={`User searched for "${searchValue}"`}
												triggerText="Request callback"
												showWrapper={true}
												layout="inline"
												className="w-full"
											/>
										</div>
									)}
								</>
							)}
						</section>
					)}
				</DrawerBody>
			</DrawerContent>
		</DrawerRoot>
	);
};

const LoadingShimmer = () => (
	<section className="px-4 pt-6 md:px-64 scrollbar-hide">
		<div className="space-y-6">
			{[0, 1].map((group) => (
				<div key={group} className="space-y-4">
					<div className="h-8 w-48 rounded-md bg-gray-200 animate-pulse" />
					<div className="space-y-3 rounded-xl bg-white p-4 shadow-sm">
						{[0, 1, 2].map((item) => (
							<div
								key={item}
								className="h-6 w-full rounded-md bg-gray-100 animate-pulse"
							/>
						))}
					</div>
				</div>
			))}
		</div>
	</section>
);

export default SearchModal;
